import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, useTheme, alpha } from "@mui/material";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { usePanelTokens } from "../../../utils/panelTokens";
import { useAuthGate } from "../../../hooks/useAuthGate";
import { getCombo } from "./api";
import { basePath, comboTitle, delta, joinRows, FREE_THROTTLE_MAX } from "./catalog";
import { ROUTES } from "../../../routes";

const CLAIMED_COLOR = "#e0a11b";
const MEASURED_COLOR = "#2e9d5a";

const fmt = (v, digits = 0) => (v == null ? "—" : Number(v).toFixed(digits));

/** Delta cell — green when we beat the claim, red when we fall short. */
const DeltaCell = ({ measured, claimed, t, invert = false }) => {
  const d = delta(measured, claimed);
  if (d == null) return <Box sx={{ color: t.textFaint }}>—</Box>;
  const good = invert ? d <= 0 : d >= 0;
  return (
    <Box sx={{ color: Math.abs(d) < 1 ? t.textMuted : good ? t.success : "#e5484d", fontWeight: 600 }}>
      {d > 0 ? "+" : ""}
      {d.toFixed(1)}%
    </Box>
  );
};

const MotorDataDetail = () => {
  const { slug } = useParams();
  const t = usePanelTokens();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, requireSignin } = useAuthGate();
  const [combo, setCombo] = useState(undefined);

  useEffect(() => {
    let alive = true;
    getCombo(slug, isAuthenticated).then((c) => alive && setCombo(c));
    return () => {
      alive = false;
    };
  }, [slug, isAuthenticated]);

  if (combo === undefined) {
    return (
      <Box sx={{ background: t.ground, minHeight: "100%", display: "flex", justifyContent: "center", pt: 10 }}>
        <CircularProgress size={26} sx={{ color: t.accent }} />
      </Box>
    );
  }

  if (!combo) {
    return (
      <Box sx={{ background: t.ground, color: t.text, minHeight: "100%", p: 7, textAlign: "center" }}>
        <Box sx={{ fontSize: 20, fontWeight: 700, color: t.textStrong, mb: 2 }}>Combination not found</Box>
        <Button component={RouterLink} to={basePath()} sx={{ color: t.accent, textTransform: "none" }}>
          Back to all test data
        </Button>
      </Box>
    );
  }

  // The server already truncated an anonymous read, so `rows` is what we are
  // entitled to see. The client-side filter is a belt-and-braces guard for the
  // seeded fallback, which has no server to enforce anything.
  const rows = joinRows(combo.claimed, combo.measured);
  const visibleRows = isAuthenticated ? rows : rows.filter((r) => r.throttle <= FREE_THROTTLE_MAX);
  // Prefer the server's count: it knows about rows it never sent.
  const hiddenCount = isAuthenticated ? 0 : combo.hiddenPointCount ?? rows.length - visibleRows.length;
  const hasMeasured = Boolean(combo.measured?.length);

  // Chart runs on the same rows the table shows, so a logged-out visitor never
  // sees a curve that extends past the gate.
  const chartData = visibleRows.map((r) => ({
    throttle: r.throttle,
    claimedThrust: r.claimed?.thrust ?? null,
    measuredThrust: r.measured?.thrust ?? null,
    claimedEff: r.claimed?.efficiency ?? null,
    measuredEff: r.measured?.efficiency ?? null,
  }));

  const grid = alpha(theme.palette.divider, 0.5);
  const axisTick = { fontSize: 11, fill: theme.palette.text.secondary };
  const tooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    fontSize: 12,
  };

  const th = { textAlign: "right", padding: "10px 12px", fontSize: 11, letterSpacing: ".06em", color: t.textFaint, fontWeight: 700, whiteSpace: "nowrap" };
  const td = { textAlign: "right", padding: "9px 12px", fontSize: 13.5, whiteSpace: "nowrap" };

  return (
    <Box sx={{ background: t.ground, color: t.text, minHeight: "100%" }}>
      <Box sx={{ px: { xs: 3, md: 7 }, pt: { xs: 4, md: 6 }, pb: { xs: 4, md: 5 }, borderBottom: `1px solid ${t.border}` }}>
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          <Button
            component={RouterLink}
            to={basePath()}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{ color: t.textMuted, textTransform: "none", mb: 2, ml: -1, "&:hover": { color: t.accent, background: "transparent" } }}
          >
            All test data
          </Button>

          <Box component="h1" sx={{ m: 0, fontSize: { xs: 26, md: 36 }, fontWeight: 700, color: t.textStrong, letterSpacing: "-0.01em" }}>
            {comboTitle(combo)}
          </Box>
          <Box sx={{ mt: 1, fontSize: 14.5, color: t.textMuted }}>
            {combo.cells ? `${combo.cells} · ` : ""}
            {hasMeasured ? "Manufacturer claim vs Vecros thrust-stand measurement" : "Manufacturer claim — Vecros bench test pending"}
          </Box>

          {Object.keys(combo.specs || {}).length > 0 && (
            <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1.2 }}>
              {Object.entries(combo.specs).map(([k, v]) => (
                <Box key={k} sx={{ px: 1.5, py: 0.8, borderRadius: 1.5, background: t.chipBg, border: `1px solid ${t.chipBorder}`, fontSize: 12.5 }}>
                  <Box component="span" sx={{ color: t.textFaint }}>{k} </Box>
                  <Box component="span" sx={{ color: t.textStrong, fontWeight: 600 }}>{v}</Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 7 }, py: { xs: 4, md: 5 }, maxWidth: 1100, mx: "auto" }}>
        {!hasMeasured && (
          <Box sx={{ mb: 4, p: 3, borderRadius: 2, border: `1px solid ${t.border}`, background: t.surfaceAlt, display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <ScienceOutlinedIcon sx={{ color: t.accent, fontSize: 22, mt: 0.2 }} />
            <Box sx={{ flex: "1 1 320px" }}>
              <Box sx={{ fontSize: 15, fontWeight: 700, color: t.textStrong, mb: 0.5 }}>
                We haven't run this one yet
              </Box>
              <Box sx={{ fontSize: 13.5, color: t.textMuted, lineHeight: 1.6 }}>
                The table below is the manufacturer's published data, reproduced as
                published. Nothing here is a Vecros measurement — when we bench it,
                a measured column and a delta appear alongside.
              </Box>
            </Box>
            <Button
              onClick={() => requireSignin(() => {})}
              variant="contained"
              sx={{ background: t.accent, color: t.onAccent, textTransform: "none", fontWeight: 600, borderRadius: 2, "&:hover": { background: t.accentHover } }}
            >
              Request this test
            </Button>
          </Box>
        )}

        {/* Entry animation is off on these Lines deliberately: recharts drives
            it by rewriting stroke-dasharray, which both fights the dashed
            "claimed" line and leaves the curve invisible if a re-render
            interrupts it mid-flight. The data is static, so nothing is lost. */}
        {chartData.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 4 }}>
            {[
              { title: "Thrust vs throttle (gf)", claimKey: "claimedThrust", measKey: "measuredThrust" },
              { title: "Efficiency vs throttle (g/W)", claimKey: "claimedEff", measKey: "measuredEff" },
            ].map((cfg) => (
              <Box key={cfg.title} sx={{ flex: "1 1 380px", minWidth: 290, p: 2, borderRadius: 2, border: `1px solid ${t.border}`, background: t.surface }}>
                <Box sx={{ fontSize: 13, fontWeight: 700, color: t.textStrong, mb: 1.5 }}>{cfg.title}</Box>
                <Box sx={{ width: "100%", height: 230 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 4, left: -8 }}>
                      <CartesianGrid stroke={grid} strokeDasharray="3 3" />
                      <XAxis dataKey="throttle" type="number" domain={["dataMin", "dataMax"]} tick={axisTick} unit="%" stroke={grid} />
                      <YAxis tick={axisTick} stroke={grid} width={52} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey={cfg.claimKey} name="Claimed" stroke={CLAIMED_COLOR} dot={false} strokeWidth={2} strokeDasharray="5 4" connectNulls isAnimationActive={false} />
                      <Line type="monotone" dataKey={cfg.measKey} name="Measured" stroke={MEASURED_COLOR} dot={false} strokeWidth={2} connectNulls isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ borderRadius: 2, border: `1px solid ${t.border}`, background: t.surface, overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ fontSize: 14, fontWeight: 700, color: t.textStrong }}>Throttle sweep</Box>
            <Box sx={{ display: "flex", gap: 2, fontSize: 12, color: t.textMuted }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box sx={{ width: 14, height: 2, background: CLAIMED_COLOR }} /> Claimed
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box sx={{ width: 14, height: 2, background: MEASURED_COLOR }} /> Measured
              </Box>
            </Box>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
              <Box component="thead">
                <Box component="tr" sx={{ borderBottom: `1px solid ${t.border}` }}>
                  <Box component="th" sx={{ ...th, textAlign: "left" }}>THROTTLE</Box>
                  <Box component="th" sx={th}>THRUST (gf)</Box>
                  <Box component="th" sx={th}>Δ</Box>
                  <Box component="th" sx={th}>CURRENT (A)</Box>
                  <Box component="th" sx={th}>Δ</Box>
                  <Box component="th" sx={th}>EFF (g/W)</Box>
                  <Box component="th" sx={th}>Δ</Box>
                  <Box component="th" sx={th}>RPM</Box>
                  <Box component="th" sx={th}>TORQUE (N·m)</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {visibleRows.map((r) => (
                  <Box component="tr" key={r.throttle} sx={{ borderBottom: `1px solid ${t.borderSoft}`, "&:hover": { background: t.hoverSoft } }}>
                    <Box component="td" sx={{ ...td, textAlign: "left", fontWeight: 700, color: t.textStrong }}>{r.throttle}%</Box>

                    <Box component="td" sx={td}>
                      <Box sx={{ color: CLAIMED_COLOR }}>{fmt(r.claimed?.thrust)}</Box>
                      {hasMeasured && <Box sx={{ color: MEASURED_COLOR }}>{fmt(r.measured?.thrust)}</Box>}
                    </Box>
                    <Box component="td" sx={td}><DeltaCell measured={r.measured?.thrust} claimed={r.claimed?.thrust} t={t} /></Box>

                    <Box component="td" sx={td}>
                      <Box sx={{ color: CLAIMED_COLOR }}>{fmt(r.claimed?.current, 2)}</Box>
                      {hasMeasured && <Box sx={{ color: MEASURED_COLOR }}>{fmt(r.measured?.current, 2)}</Box>}
                    </Box>
                    {/* More current for the same throttle is worse, hence invert. */}
                    <Box component="td" sx={td}><DeltaCell measured={r.measured?.current} claimed={r.claimed?.current} t={t} invert /></Box>

                    <Box component="td" sx={td}>
                      <Box sx={{ color: CLAIMED_COLOR }}>{fmt(r.claimed?.efficiency, 2)}</Box>
                      {hasMeasured && <Box sx={{ color: MEASURED_COLOR }}>{fmt(r.measured?.efficiency, 2)}</Box>}
                    </Box>
                    <Box component="td" sx={td}><DeltaCell measured={r.measured?.efficiency} claimed={r.claimed?.efficiency} t={t} /></Box>

                    <Box component="td" sx={{ ...td, color: t.textMuted }}>
                      <Box>{fmt(r.claimed?.rpm)}</Box>
                      {hasMeasured && <Box>{fmt(r.measured?.rpm)}</Box>}
                    </Box>
                    <Box component="td" sx={{ ...td, color: t.textMuted }}>
                      <Box>{fmt(r.claimed?.torque, 2)}</Box>
                      {hasMeasured && <Box>{fmt(r.measured?.torque, 2)}</Box>}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {hiddenCount > 0 && (
            <Box sx={{ p: { xs: 3, md: 4 }, textAlign: "center", borderTop: `1px solid ${t.border}`, background: `${t.accent}0A` }}>
              <LockOutlinedIcon sx={{ color: t.accent, fontSize: 26, mb: 1 }} />
              <Box sx={{ fontSize: 17, fontWeight: 700, color: t.textStrong, mb: 1 }}>
                {hiddenCount} more throttle {hiddenCount === 1 ? "point" : "points"} above {FREE_THROTTLE_MAX}%
              </Box>
              <Box sx={{ fontSize: 14, color: t.textMuted, maxWidth: 520, mx: "auto", mb: 2.5, lineHeight: 1.6 }}>
                The high-throttle end is where published numbers and bench numbers
                part ways — heat soak, sag and efficiency fall-off all show up here.
                A free account unlocks the rest of the sweep.
              </Box>
              <Button
                onClick={() => requireSignin(() => {})}
                variant="contained"
                sx={{ background: t.accent, color: t.onAccent, textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3, "&:hover": { background: t.accentHover } }}
              >
                Unlock the full sweep — free
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 3, p: 3, borderRadius: 2, border: `1px solid ${t.border}`, background: t.surface }}>
          <Box sx={{ fontSize: 14, fontWeight: 700, color: t.textStrong, mb: 1.5 }}>Test conditions &amp; methodology</Box>
          {isAuthenticated ? (
            <Box sx={{ fontSize: 13.5, color: t.textMuted, lineHeight: 1.7, whiteSpace: "pre-line" }}>
              {combo.methodology ||
                (combo.claimedTemp
                  ? `Manufacturer states ${combo.claimedTemp.temp} °C motor surface temperature after ${combo.claimedTemp.minutes} min at ${combo.claimedTemp.throttle}% throttle, ${combo.claimedTemp.ambient} °C ambient. Vecros bench conditions are published alongside each measured run.`
                  : "Methodology for this combination has not been published yet.")}
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ flex: "1 1 320px", fontSize: 13.5, color: t.textMuted, lineHeight: 1.6 }}>
                Mount, ESC, ambient temperature, run duration, settling time and
                calibration — everything needed to reproduce or challenge these
                numbers. Sign in to read it.
              </Box>
              <Button
                onClick={() => requireSignin(() => {})}
                startIcon={<LockOutlinedIcon sx={{ fontSize: 16 }} />}
                variant="outlined"
                sx={{ color: t.accent, borderColor: t.accent, textTransform: "none", fontWeight: 600, borderRadius: 2, "&:hover": { borderColor: t.accent, background: `${t.accent}14` } }}
              >
                Sign in to view
              </Button>
            </Box>
          )}
        </Box>

        {combo.claimedSource && (
          <Box sx={{ mt: 2, fontSize: 12.5, color: t.textFaint }}>
            Claimed figures reproduced from the manufacturer's published test data —{" "}
            <Box
              component="a"
              href={combo.claimedSource}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: t.accent, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              source
            </Box>
            .
          </Box>
        )}

        <Box sx={{ mt: 4, p: { xs: 3, md: 4 }, borderRadius: 2, border: `1px solid ${t.accent}`, background: `${t.accent}0F`, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ flex: "1 1 340px" }}>
            <Box sx={{ fontSize: 18, fontWeight: 700, color: t.textStrong, mb: 0.8 }}>Need the raw bench data?</Box>
            <Box sx={{ fontSize: 14, color: t.textMuted, lineHeight: 1.6 }}>
              Full-rate CSV — every sample, temperature trace and vibration channel,
              plus every combination in the library. Available on a paid plan.
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              onClick={() => requireSignin(() => navigate(ROUTES.plans))}
              startIcon={<DownloadOutlinedIcon sx={{ fontSize: 17 }} />}
              variant="contained"
              sx={{ background: t.accent, color: t.onAccent, textTransform: "none", fontWeight: 600, borderRadius: 2, "&:hover": { background: t.accentHover } }}
            >
              See plans
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MotorDataDetail;
